import React, { useEffect, useState } from "react";
import styles from "./AuthLayout.module.scss";
import Notifier from "@features/common/notifier/Notifier";
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import LoginFooter from "@features/common/footer/loginFooter/LoginFooter";
import { useGetTokenMutation } from "features/auth/authApi";
import { useAppDispatch } from "@redux/hooks";
import { setAccessToken, setoAuthAccessToken } from "@redux/slices/AuthSlice";
import PageLoader from "@features/common/pageLoader/PageLoader";
import NoInternet from "@features/common/noInternet/NoInternet";
import { useFetchProfileQuery } from "@features/common/commonApi";
import { getFirstAccessibleRoute } from "@utils/redirectUtils";
import { PAGE_URLS } from "@constants/common";
import API_URLS, { API_BASE_URL } from "@constants/apiUrls";
import { setShowAvailableBalance } from "@redux/slices/CommonSlice";

const attemptTokenRefresh = async () => {
  try {
    const response = await fetch(API_BASE_URL + API_URLS.REFRESH_TOKEN, {
      method: "POST",
      credentials: "include",
    });

    if (response.status === 400) {
      return { error400: true };
    }

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data?.access) {
      return null;
    }
    return { access: data.access };
  } catch (error) {
    return null;
  }
};

const AuthLayout: React.FC = () => {
  /* --------------------------------------------------------------------------
   * Hooks
   * -------------------------------------------------------------------------- */
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { refetch: refetchProfile } = useFetchProfileQuery();
  // No Internet logic
  const [hasInternet, setHasInternet] = useState(window.navigator.onLine);
  const [isVerifying, setIsVerifying] = useState(true);

  useEffect(() => {
    const updateNetwork = () => setHasInternet(window.navigator.onLine);
    window.addEventListener("offline", updateNetwork);
    window.addEventListener("online", updateNetwork);
    return () => {
      window.removeEventListener("offline", updateNetwork);
      window.removeEventListener("online", updateNetwork);
    };
  }, []);

  /* --------------------------------------------------------------------------
   * API Mutation
   * -------------------------------------------------------------------------- */
  /**
   * OAuth token mutation for client credentials grant
   * Retrieves access token required for authentication APIs
   */
  const [getToken, { data, isSuccess, isLoading, isError }] =
    useGetTokenMutation();

  /* --------------------------------------------------------------------------
   * Effects
   * -------------------------------------------------------------------------- */
  /**
   * Fetch OAuth access token on component mount
   * Uses client credentials from environment variables
   * Required for subsequent authentication API calls
   */
  useEffect(() => {
    getToken({
      client_id: import.meta.env.VITE_MYCREDITS_CONSOLE_OAUTH_CLIENT_ID,
      client_secret: import.meta.env.VITE_MYCREDITS_CONSOLE_OAUTH_CLIENT_SECRET,
      grant_type: "client_credentials",
    });
  }, []);

  /**
   * Store OAuth access token in Redux state when successfully fetched
   * Makes token available globally for authenticated API requests
   */

  /**
   * Verifies user login status and handles automatic navigation on "/" route load
   *
   * Purpose:
   * - When user loads "/" route, determine if they're logged in
   * - If not logged in: stay on login page
   * - If logged in: redirect to appropriate page based on role and corporate selection
   *
   * Flow:
   * 1. Check if user has valid refresh token (auto-login)
   * 2. If valid, fetch user profile to get navigation permissions
   * 3. Route to first accessible page or corporate selection screen
   */

  const verifyUserLogin = async () => {
    // Show loading spinner while verification is in progress
    setIsVerifying(true);
    try {
      // Step 1: Attempt to refresh access token using refresh token cookie
      // This will succeed if user has a valid session, fail if logged out
      const result = await attemptTokenRefresh();

      // Step 2: If refresh token is valid and we got a new access token
      if (result?.access) {
        // Store the new access token in Redux for API calls
        dispatch(setAccessToken(result.access));

        try {
          // Step 3: Fetch user profile to get navigation permissions and corporate info
          const { data } = await refetchProfile();
          const enabled_navigation = data?.data?.enabled_navigation;

          const wallet_balance = enabled_navigation?.wallet_balance;

          dispatch(setShowAvailableBalance(wallet_balance || false));

          // Step 4: If no navigation permissions returned, send to corporate selection
          // This happens when user hasn't selected a corporate yet
          if (!enabled_navigation) {
            navigate(PAGE_URLS.SELECT_CORPORATE, { replace: true });
            return; // Exit early after navigation
          }

          // Step 5: Check both selected_corporate and show_corporate_selection flags to prevent redirecting to corporate selection page on "/" reload
          const hasCorporateSelected =
            !!localStorage.getItem("selected_corporate") ||
            localStorage.getItem("show_corporate_selection") === "true";

          // Step 6a: If corporate is selected, route to first accessible page based on role
          if (hasCorporateSelected) {
            // Get first page user has permission to access (based on enabled_navigation)
            const firstRoute = getFirstAccessibleRoute(enabled_navigation);
            navigate(firstRoute, { replace: true });
            return; // Exit after navigation
          } else {
            // Step 6b: If no corporate selected, send to corporate selection screen
            navigate(PAGE_URLS.SELECT_CORPORATE, { replace: true });
            return; // Exit after navigation
          }
        } catch (profileError) {
          // Step 7: Profile fetch failed (network error, server error, etc.)
          console.error(
            "Failed to fetch profile after token refresh:",
            profileError,
          );
          // Profile fetch failed — stay on login
        }
      } else {
        if (pathname === "/" || pathname === PAGE_URLS.LOGIN) {
          // No valid refresh token — stay on login page
          // Only navigate if we're on root or login to prevent disrupting other routes
          navigate(PAGE_URLS.LOGIN, { replace: true });
        }
      }
      // result is null or error400 — stay on login, do nothing
    } catch (refreshError) {
      console.error("Token refresh failed:", refreshError);
      // Refresh threw unexpectedly — stay on login
    } finally {
      // Step 8: Always hide loading spinner when verification completes
      // This runs even if we return early from inside try blocks
      setIsVerifying(false);
    }
  };

  useEffect(() => {
    if (isSuccess && data?.access_token) {
      dispatch(setoAuthAccessToken(data.access_token));
      verifyUserLogin();
    }
  }, [data, isSuccess, dispatch]);

  if ((isLoading || isVerifying) && !isError) {
    return (
      <div style={{}}>
        <PageLoader />
      </div>
    );
  }

  /* --------------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------------- */
  return (
    <div
      className={`signin-signup ${styles["loginlayout"]}`}
      style={{
        backgroundImage: `url(/images/login-bg-image.png)`,
      }}
    >
      {/* Toast Notifications */}
      <Notifier />

      {/* No Internet Banner */}
      <NoInternet isOnline={hasInternet} />

      {/* Child Auth Routes (Login, Signup, etc.) */}
      <Outlet context={{ redirectUrl: searchParams.get("redirect") }} />

      {/* Footer with Privacy Policy and Terms Links */}
      <LoginFooter />
    </div>
  );
};

export default AuthLayout;
